import { useCallback, useMemo, useState } from 'react';

import type { FaultedDevice } from '@/features/equipment-browser/faulted-devices/faulted-devices.types';

import { useCurrentModes } from './use-current-modes';
import type { ModeValuesByDeviceId } from '../reset-faulted.types';
import { isSelectable, resolveTargetModes } from '../reset-faulted.utils';

interface UseResetFaultedSelectionResult {
  /** Removes devices from the selection (after a successful reset or a discard). */
  deselectDevices: (deviceIds: string[]) => void;
  /** Selected ids, limited to devices still faulted and selectable. */
  selectedDeviceIds: string[];
  selectedDevices: FaultedDevice[];
  setSelectedDeviceIds: (deviceIds: string[]) => void;
  setTargetMode: (deviceId: string, mode: string) => void;
  /** Mode each device will run in after reset: the user's choice, else its current mode. */
  targetModes: ModeValuesByDeviceId;
}

/**
 * Row selection and per-device mode choice for the Reset Faulted pane. The
 * selection is intersected with the live faulted list, and ids that drop out
 * (a device recovered or became unselectable) are pruned from state so a
 * device that faults again comes back unselected.
 */
export const useResetFaultedSelection = (
  devices: FaultedDevice[]
): UseResetFaultedSelectionResult => {
  const [rawSelectedIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [chosenModes, setChosenModes] = useState<ModeValuesByDeviceId>({});

  const currentModes = useCurrentModes(devices);

  const targetModes = useMemo(
    () => resolveTargetModes(devices, currentModes, chosenModes),
    [devices, currentModes, chosenModes]
  );

  const selectedDevices = useMemo(
    () =>
      devices.filter(
        (device) =>
          rawSelectedIds.includes(device.summary.referenceId) && isSelectable(device, targetModes)
      ),
    [devices, rawSelectedIds, targetModes]
  );

  const selectedDeviceIds = useMemo(
    () => selectedDevices.map((device) => device.summary.referenceId),
    [selectedDevices]
  );

  // selectedDeviceIds is a subset of rawSelectedIds, so a length mismatch means stale ids.
  if (selectedDeviceIds.length !== rawSelectedIds.length) {
    setSelectedDeviceIds(selectedDeviceIds);
  }

  const deselectDevices = useCallback((deviceIds: string[]) => {
    setSelectedDeviceIds((prev) => prev.filter((id) => !deviceIds.includes(id)));
  }, []);

  const setTargetMode = useCallback((deviceId: string, mode: string) => {
    setChosenModes((prev) => ({ ...prev, [deviceId]: mode }));
  }, []);

  return {
    deselectDevices,
    selectedDeviceIds,
    selectedDevices,
    setSelectedDeviceIds,
    setTargetMode,
    targetModes,
  };
};
